import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HiOutlineSparkles,
  HiOutlineCog,
  HiOutlineSearch,
  HiOutlineChartPie,
  HiOutlineClipboardList,
  HiOutlineChevronRight,
  HiOutlineChevronLeft,
  HiOutlineArrowRight,
  HiOutlineUsers,
  HiOutlineOfficeBuilding,
  HiOutlineCurrencyDollar,
  HiOutlineMail,
  HiOutlineCube,
  HiOutlineFlag,
  HiOutlineUpload,
  HiOutlineDocumentReport,
} from 'react-icons/hi';
import Modal from '../shared/Modal';
import Confetti from '../shared/Confetti';
import { useAuth, welcomeTourSeenKey } from '../../context/AuthContext';

/**
 * One-time first-run tour, shown on the home page the first time a user lands
 * there — the founding admin straight out of the registration wizard, and every
 * staff member an admin adds later.
 *
 * Mounted in MainLayout rather than in Dashboard: the home route resolves to one
 * of three components depending on the user's launch_page (company overview,
 * user dashboard, or an embedded Q AI dashboard), and the tour has to fire for
 * all three.
 */

const SETTINGS_LINKS = [
  {
    icon: HiOutlineUsers,
    label: 'Users Roles & Management',
    to: '/settings/users-roles',
    blurb: 'Invite your team, assign roles, and control what each person can see.',
  },
  {
    icon: HiOutlineOfficeBuilding,
    label: 'General → Company',
    to: '/settings/general?section=company-details',
    blurb: 'Company details, branch addressing, and your logo under Document Branding.',
  },
  {
    icon: HiOutlineCurrencyDollar,
    label: 'General → Currency & FX',
    to: '/settings/general?section=currency-setup',
    blurb: 'Set your base currency and keep exchange rates up to date.',
  },
  {
    icon: HiOutlineMail,
    label: 'General → Email Config',
    to: '/settings/general?section=integrations',
    blurb: 'Connect the mailbox that sends invoices, statements and notifications.',
  },
  {
    icon: HiOutlineCube,
    label: 'Inventory Defaults → Costing & Valuation',
    to: '/settings/inventory-defaults',
    blurb: 'Choose your costing method — FIFO, Weighted Average or Standard Cost.',
  },
  {
    icon: HiOutlineFlag,
    label: 'License & Feature Flags',
    to: '/settings/feature-flags',
    blurb: 'Switch optional modules on or off so the app only shows what you use.',
  },
  {
    icon: HiOutlineUpload,
    label: 'Master Data - Import',
    to: '/settings/master-data-import',
    blurb: 'Bring customers, suppliers, items and opening balances in from a spreadsheet.',
  },
  {
    icon: HiOutlineDocumentReport,
    label: 'Report Customization',
    to: '/settings/report-layouts',
    blurb: 'Put your own branding and layout on the documents your customers receive.',
  },
];

const QAI_PROMPT_GROUPS = [
  { heading: 'Business', prompts: ['Summarize my business today', 'What is my total revenue this month?'] },
  { heading: 'Customers', prompts: ['Which customer owes the most money?', 'Show customers on credit hold'] },
  { heading: 'Inventory', prompts: ['Items below reorder level', 'Total inventory valuation'] },
];

const firstNameOf = (user) => {
  const full = String(user?.full_name || user?.username || '').trim();
  if (!full) return 'there';
  return full.split(/\s+/)[0];
};

/** A static, decorative miniature of the real sidebar — not a live component. */
const SidebarSketch = () => (
  <div
    aria-hidden="true"
    className="w-full sm:w-40 shrink-0 rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-slate-800 dark:bg-slate-950/60"
  >
    <div className="mb-2 flex items-center gap-1.5 rounded border border-gray-200 bg-white px-1.5 py-1 dark:border-slate-700 dark:bg-slate-900">
      <HiOutlineSearch className="h-3 w-3 text-gray-400 dark:text-slate-500" />
      <span className="text-[10px] text-gray-400 dark:text-slate-500">Search modules...</span>
    </div>
    <div className="space-y-1">
      <div className="flex items-center gap-1.5 rounded bg-emerald-500/10 px-1.5 py-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">
        <HiOutlineChartPie className="h-3 w-3" /> Dashboard
      </div>
      <div className="flex items-center gap-1.5 px-1.5 py-1 text-[10px] text-gray-500 dark:text-slate-400">
        <HiOutlineClipboardList className="h-3 w-3" /> My To-Do
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center justify-between px-1.5 py-1">
          <span className="h-1.5 rounded bg-gray-300 dark:bg-slate-700" style={{ width: `${58 - i * 10}%` }} />
          <HiOutlineChevronRight className="h-2.5 w-2.5 text-gray-300 dark:text-slate-700" />
        </div>
      ))}
      <div className="mt-1 flex items-center gap-1.5 border-t border-gray-200 px-1.5 pt-1.5 text-[10px] text-gray-500 dark:border-slate-800 dark:text-slate-400">
        <HiOutlineCog className="h-3 w-3" /> Settings
      </div>
    </div>
  </div>
);

const WelcomeTour = () => {
  const { user, isAuthenticated, isAiEnabled, markWelcomeTourSeen } = useAuth();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  // Once per mount, and only ever latched at the moment the tour actually
  // opens. Dismissing normally is already self-suppressing (the flag and the
  // localStorage key both flip), but a browser with storage blocked on a tenant
  // that predates migration 230 has neither — without this latch the effect
  // would re-open the modal on the next render.
  const shownRef = useRef(false);

  useEffect(() => {
    if (shownRef.current) return;
    if (!isAuthenticated || !user?.id) return;
    // Home page only. Someone who signs in and deep-links straight into a task
    // gets the tour on their next visit to '/', not on top of what they opened.
    if (location.pathname !== '/') return;
    // Never stack on top of the forced password-change flow.
    if (user.must_change_password) return;
    // `true` means the server knows they have seen it. `undefined` means the
    // tenant predates migration 230 — fall through to the local flag.
    if (user.has_seen_welcome_tour === true) return;

    const key = welcomeTourSeenKey(user);
    try {
      if (key && localStorage.getItem(key)) return;
    } catch {
      // Storage unavailable — showing the tour once more beats never showing it.
    }

    shownRef.current = true;
    setIsOpen(true);
  }, [isAuthenticated, user, location.pathname]);

  // Closing is one-way: skipping, finishing, following a link out of the tour,
  // and dismissing via the X or the backdrop all count as "seen". Anything else
  // would show a brand-new user the same modal again on their next page load.
  const finish = useCallback(() => {
    setIsOpen(false);
    markWelcomeTourSeen();
  }, [markWelcomeTourSeen]);

  const slides = useMemo(() => {
    const list = [
      {
        key: 'welcome',
        title: `Welcome, ${firstNameOf(user)}`,
        render: () => (
          <div className="py-4 text-center">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <HiOutlineSparkles className="h-7 w-7" />
            </div>
            <p className="font-display text-2xl font-bold leading-snug text-navy-800 dark:text-slate-100">
              Your business journey, reimagined with intelligence.
            </p>
            <p className="mx-auto mt-4 max-w-md text-sm text-gray-500 dark:text-slate-400">
              {user?.company_name ? `${user.company_name} is ready to go.` : 'Your workspace is ready.'}{' '}
              Take sixty seconds and we&apos;ll show you around — where everything lives, what to set
              up first, and how to just ask for what you need.
            </p>
          </div>
        ),
      },
      {
        key: 'navigation',
        title: 'Finding your way around',
        render: () => (
          <div className="flex flex-col gap-5 py-2 sm:flex-row">
            <SidebarSketch />
            <div className="min-w-0 flex-1 space-y-3 text-sm text-gray-600 dark:text-slate-300">
              <p>
                Everything lives in the <strong className="text-navy-800 dark:text-slate-100">sidebar on the
                left</strong>. <strong className="text-navy-800 dark:text-slate-100">Dashboard</strong> and{' '}
                <strong className="text-navy-800 dark:text-slate-100">My To-Do</strong> sit at the top,{' '}
                <strong className="text-navy-800 dark:text-slate-100">Settings</strong> at the very bottom.
              </p>
              <p>
                In between are your business areas. Click one and it expands to reveal its pages —
                and most carry their own <strong className="text-navy-800 dark:text-slate-100">Reports</strong>{' '}
                section nested inside.
              </p>
              <p>
                In a hurry? Type into <strong className="text-navy-800 dark:text-slate-100">Search
                modules...</strong> at the top of the sidebar to jump straight to any page.
              </p>
              <p className="text-gray-500 dark:text-slate-400">
                Need more room? <strong className="text-navy-800 dark:text-slate-100">← Collapse</strong> at
                the bottom shrinks the bar to icons. On a phone, tap the menu button in the header instead.
              </p>
            </div>
          </div>
        ),
      },
      {
        key: 'settings',
        title: 'Set up your workspace in Settings',
        render: () => (
          <div className="py-1">
            <p className="mb-4 text-sm text-gray-600 dark:text-slate-300">
              Open <Link to="/settings/general" onClick={() => finish()} className="font-semibold text-emerald-600 underline-offset-2 hover:underline dark:text-emerald-400">Settings</Link>{' '}
              from the bottom of the sidebar. This is where you make Journey.AI yours:
            </p>
            <ul className="space-y-2.5">
              {SETTINGS_LINKS.map(({ icon: Icon, label, to, blurb }) => (
                <li key={to}>
                  <Link
                    to={to}
                    onClick={() => finish()}
                    className="flex items-start gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-gray-50 dark:hover:bg-slate-800/60"
                  >
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-navy-800 dark:text-slate-100">{label}</span>
                      <span className="block text-xs text-gray-500 dark:text-slate-400">{blurb}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-gray-400 dark:text-slate-500">
              Some of these are reserved for administrators, so you may not see every option.
            </p>
          </div>
        ),
      },
    ];

    // The copilot is gated per tenant; when AI is off there is no "Ask Q AI"
    // button in the header at all, so promising one would be a lie. Dropping the
    // slide is enough — the dots and the final button both derive from length.
    if (isAiEnabled) {
      list.push({
        key: 'qai',
        title: 'Meet Q AI, your Business Copilot',
        render: () => (
          <div className="py-1">
            <p className="text-sm text-gray-600 dark:text-slate-300">
              Click <strong className="text-navy-800 dark:text-slate-100">Ask Q AI</strong> in the header —
              or press{' '}
              <kbd className="rounded border border-gray-300 bg-gray-100 px-1.5 py-0.5 font-sans text-xs font-semibold text-gray-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">Ctrl</kbd>
              {' + '}
              <kbd className="rounded border border-gray-300 bg-gray-100 px-1.5 py-0.5 font-sans text-xs font-semibold text-gray-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">Q</kbd>{' '}
              from anywhere — and ask a question in plain English. Q reads your live data and answers
              with a number, a table or a chart, and can point you straight at the screen you need.
            </p>
            <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500">
              Try asking
            </p>
            <div className="mt-2 space-y-3">
              {QAI_PROMPT_GROUPS.map(({ heading, prompts }) => (
                <div key={heading}>
                  <p className="mb-1.5 text-xs font-semibold text-navy-800 dark:text-slate-200">{heading}</p>
                  <div className="flex flex-wrap gap-2">
                    {prompts.map((p) => (
                      <span
                        key={p}
                        className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-600 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-300"
                      >
                        &ldquo;{p}&rdquo;
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ),
      });
    }

    return list;
  }, [user, isAiEnabled, finish]);

  if (!isOpen || !slides.length) return null;

  const index = Math.min(step, slides.length - 1);
  const isLast = index >= slides.length - 1;
  const slide = slides[index];

  // The house Modal lays its footer out `justify-end`; the w-full wrapper is
  // what puts Skip and the step dots on the left and Back / Next on the right.
  const footer = (
    <div className="flex w-full items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={finish}
          className="text-sm text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          Skip
        </button>
        <div className="flex items-center gap-1.5" aria-hidden="true">
          {slides.map((s, i) => (
            <span
              key={s.key}
              className={`h-1.5 rounded-full transition-all ${
                i === index
                  ? 'w-5 bg-emerald-500'
                  : 'w-1.5 bg-gray-300 dark:bg-slate-700'
              }`}
            />
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {index > 0 && (
          <button
            type="button"
            onClick={() => setStep((prev) => Math.max(0, prev - 1))}
            className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <HiOutlineChevronLeft className="h-4 w-4" /> Back
          </button>
        )}
        <button
          type="button"
          onClick={() => (isLast ? finish() : setStep((prev) => prev + 1))}
          className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-600"
        >
          {isLast ? 'Get started' : 'Next'}
          <HiOutlineArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Modal isOpen size="lg" title={slide.title} onClose={finish} footer={footer}>
        {slide.render()}
      </Modal>
      {/* After the Modal in the DOM so the burst reads over the dialog rather
          than behind its backdrop — both sit at z-50, so paint order decides.
          The canvas is pointer-events-none, so the buttons stay clickable. */}
      {index === 0 && <Confetti active />}
    </>
  );
};

export default WelcomeTour;
